import { useState } from 'react'
import { ChevronDown, MapPin } from 'lucide-react'
import { Button, Checkbox } from '../common'
import { Popover } from '../common/overlays/Popover'
import { statesParam } from './dashboardFilters'
import { listedStates, statesLabel } from './stateFilter'
import widgetStyles from './widgets/shared/Widgets.module.css'
import styles from './DashboardFilterControls.module.css'

// Which of the brand's states the page reads: every one until some are
// picked. Picked states are added together on every widget; the per-state
// lines on a trend show them side by side. Picks are kept here while the
// list is open and applied once, when it closes, so ticking three states
// reloads the page once rather than three times.
export function StatePicker({ states, picked, onChange, compact = false }) {
  const [draft, setDraft] = useState(null)
  const shown = draft ?? picked
  const shownSet = new Set(shown)
  const listed = listedStates(states, shown)
  const label = statesLabel(shown, states, { shown: compact ? 2 : 3 })

  const setOpen = (open) => {
    if (open) {
      setDraft(picked)
      return
    }
    if (draft && statesParam(draft) !== statesParam(picked)) onChange(draft)
    setDraft(null)
  }
  const toggle = (key) => {
    setDraft(shownSet.has(key) ? shown.filter((item) => item !== key) : [...shown, key])
  }

  return (
    <Popover
      open={draft !== null}
      onOpenChange={setOpen}
      placement="bottom-start"
      label="States"
      contentClassName={widgetStyles.pickerSurface}
      trigger={(
        <button type="button" className={`${styles.trigger} ${styles.stateTrigger}`} aria-label={`States: ${label}`} data-picked={shown.length || undefined}>
          <MapPin size={16} aria-hidden="true" />
          <span className={styles.triggerLabel}>{label}</span>
          <ChevronDown size={16} aria-hidden="true" />
        </button>
      )}
    >
      <div className={widgetStyles.picker}>
        <div className={styles.stateHeader}>
          <p className={widgetStyles.pickerHint}>Pick one or more states. Several are added together.</p>
          <button type="button" className={styles.stateClear} disabled={!shown.length} onClick={() => setDraft([])}>All states</button>
        </div>
        <ul className={widgetStyles.pickerList} aria-label="States">
          {listed.map((state) => (
            <li key={state.key} className={widgetStyles.pickerItem}>
              <Checkbox
                checked={shownSet.has(state.key)}
                onChange={() => toggle(state.key)}
                label={(
                  <span className={styles.stateName}>
                    {state.label}
                    {state.code ? <span className={styles.stateCode}>{state.code}</span> : null}
                  </span>
                )}
              />
            </li>
          ))}
        </ul>
        <div className={styles.stateActions}>
          <Button variant="primary" size="compact" onClick={() => setOpen(false)}>Done</Button>
        </div>
      </div>
    </Popover>
  )
}
